import React from 'react';

import PropTypes from 'prop-types';

import clsx from 'clsx';

import {
    ColorSwatch,
    Icon,
    ListColumns,
    Text,
} from 'reaxl';
import { useFeatures } from 'reaxl-features';
import { InfoIcon } from 'reaxl-molecules';

/*
 *  Builds specifications line for vehicle reconditioning data
 */
const getFormattedVehicleReconditioningSpecs = (vehicleReconditioningSpecs) => {
    const vehicleReconditioningValuesMap = {
        BRAKES: 'New Brakes',
        DETAILING: 'Detailed',
        TIRES: 'New Tires',
    };
    const key = 'vehicleReconditioning';

    if (vehicleReconditioningSpecs?.services?.length) {
        return {
            key,
            specData: {
                value: vehicleReconditioningSpecs.services.reduce((acc, curr, i) => {
                    if (i === 0) {
                        acc += vehicleReconditioningValuesMap[curr.servicePerformed];
                    } else if (i === 1) {
                        acc += `, ${vehicleReconditioningValuesMap[curr.servicePerformed]}`;
                    } else if (i === 2) {
                        acc += '...';
                    }
                    return acc;
                }, ''),
                label: 'VEHICLE RECONDITIONING',
                icon: 'detailed',
                ctaEl: (
                    <Text
                        className="text-link margin-left-2 text-normal text-size-200 align-items-center display-flex"
                        id="vehicle-reconditioning-see-details"
                    >
                        See Details
                    </Text>
                ),
            },
        };
    }

    return {
        key,
        specData: {
            emptyRow: true,
        },
    };
};

/**
 *  Creates label layout for a specification with optional tooltip
 */
function RenderDisclaimer(disclaimer) {
    return (
        <InfoIcon
            infoText={disclaimer}
            placement="bottom"
        />
    );
}

// key is icon attribute from specifications object
function SpecificationIcon(key, highlightIcon = false, { value = '', label, colorValue }) {
    let icon;
    switch (key) {
        case 'transmission':
            if (value.toLowerCase().includes('manual')) {
                icon = 'transmissionManual';
            } else {
                icon = 'transmissionAuto';
            }
            break;
        case 'driveType':
            if (value.toLowerCase().includes('front')) {
                icon = 'driveTypeFront';
            } else if (value.toLowerCase().includes('rear') && !value.toLowerCase().includes('4 wheel')) {
                icon = 'driveTypeRear';
            } else {
                icon = 'driveTypeAll';
            }
            break;
        case 'mpg':
            if (value.toLowerCase().includes('hybrid')) {
                icon = 'mpgHybrid';
            } else {
                icon = 'mpgFuel';
            }
            break;
        default:
            icon = key;
    }

    if (key === 'exterior' || key === 'interiorSeats') {
        return (
            <ColorSwatch
                className="margin-bottom-1 margin-left-1"
                colorValue={colorValue}
            />
        );
    }

    return (
        <Icon
            icon={icon}
            label={label}
            highlightIcon={highlightIcon}
        />
    );
}

const renderValues = (emptyRow, key, value, label, ctaEl, specIcon, disclaimer) => {
    if (!emptyRow) {
        return (
            <>
                {(key === 'mileage' ? value + ' miles' : value) || 'Information Unavailable'}
                {(label === 'LOW MILEAGE' && <div className="text-subdued-lightest margin-left-1">- Low Mileage</div>)}
                {ctaEl}
                {!!specIcon && !!disclaimer && RenderDisclaimer(disclaimer)}
            </>
        );
    }
    return (
        <div data-cmp="specifications-placeholder" />
    );
};

/**
 *  Table of Vehicle Specifications
 *
 */
function SpecificationList({
    vin,
    specKeys,
    specifications,
    vehicleReconditioningSpecs,
    className,
    bordered = true,
    ...rest
}) {

    const {
        battery_health: [enableBatteryHealth, {
            ford_battery_health: fordBatteryHealth,
        }],
        icons: [, {
            highlighted_icon_url: highlightedIconUrl,
            highlighted_icons: highlightedIcons,
        }],
    } = useFeatures([
        'battery_health',
        'icons',
    ]);

    const specificationItems = specKeys.reduce((items, key) => (key in specifications ? items.concat({ key, specData: specifications[key] }) : items), []);
    // Append vehicle reconditioning data to the bottom of specifications
    specificationItems.push(getFormattedVehicleReconditioningSpecs(vehicleReconditioningSpecs));

    const isElectricVehicle = specifications?.engineDescription?.value?.toLowerCase() === 'electric';

    return (
        <div
            className={className}
            {...rest}
        >
            <ListColumns
                items={specificationItems}
                xs={1}
                sm={1}
                md={2}
                lg={2}
                bordered={bordered}
                condensed
                className="padding-0"
                render={({ key, specData: { label, value, disclaimer, icon, colorValue = '', ctaEl, emptyRow = false } }) => {
                    const labelClassNames = clsx({
                        'col-xs-4 text-size-200 text-subdued-lightest': !icon,
                        'col-xs-2': !!icon,
                    });
                    const valueClassNames = clsx('display-flex', {
                        'text-subdued-lightest': !value,
                        'col-xs-8': !icon,
                        'col-xs-10 margin-bottom-0 flex-wrap gap-1': !!icon,
                    });
                    const wrapperClassNames = clsx({
                        [className]: className,
                    }, 'align-items-center display-flex row text-bold');

                    const highlightIcon = enableBatteryHealth && fordBatteryHealth && isElectricVehicle && highlightedIconUrl && highlightedIcons?.includes(icon);

                    const specIcon = SpecificationIcon(icon, highlightIcon, { value, label, colorValue });

                    return (
                        <div
                            className={wrapperClassNames}
                            {...rest}
                        >
                            <div className={labelClassNames}>
                                {specIcon || (
                                    <>
                                        {label}
                                        {!!disclaimer && RenderDisclaimer(disclaimer)}
                                    </>
                                )}
                            </div>
                            <div
                                className={valueClassNames}
                                data-cmp={key}
                            >
                                {renderValues(emptyRow, key, value, label, ctaEl, specIcon, disclaimer)}
                            </div>
                        </div>
                    );
                }}
            />
        </div>
    );
}

SpecificationList.propTypes = {
    /**
     * The className to apply to the root node
     */
    className: PropTypes.string,
    /**
     * List of matching keys for spec sources
     */
    specKeys: PropTypes.array,
    /**
     * Labels and values for specs
     */
    specifications: PropTypes.object,
    /**
     * Controls grid lines display
    */
    bordered: PropTypes.bool,
};

export default SpecificationList;
